import React, { useCallback, useRef, useEffect } from "react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import { InternalControlData, NATURE_OF_PROCEDURE_OPTIONS } from "../types";
import { Plus, Trash2, Copy } from "lucide-react";
import { ControlDocumentManager } from "../ControlDocumentManager";

interface RollforwardProps {
  data: InternalControlData;
  onChange: (data: Partial<InternalControlData>) => void;
}

export const Rollforward: React.FC<RollforwardProps> = ({ data, onChange }) => {
  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Stabilize callbacks for ControlDocumentManager to prevent infinite loops
  const handleRollforwardDocumentsChange = useCallback((documents: typeof data.rollforwardOperatingTestsDocuments) => {
    onChangeRef.current({ rollforwardOperatingTestsDocuments: documents });
  }, []);

  const handleRollforwardTextResponseChange = useCallback((textResponse: typeof data.rollforwardOperatingTestsTextResponse) => {
    onChangeRef.current({ rollforwardOperatingTestsTextResponse: textResponse });
  }, []);

  // Check if timing is "Apportion" - if so, show message
  if (data.timingOfProcedure !== "Conclude at Interim") {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg">
        <div className="border-b border-white/10 p-6">
          <h2 className="text-xl font-semibold text-white">Rollforward</h2>
        </div>
        <div className="p-6">
          <div className="p-8 border border-white/10 rounded-lg bg-black/20 text-center">
            <p className="text-white/60 text-lg">
              Timing of Procedure is Apportion
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Generate steps from Control Summary if detailed description is enabled
  const controlSteps = data.detailedDescriptionEnabled ? data.steps : [];

  const addInquiry = () => {
    const newInquiry = {
      id: `inquiry-${Date.now()}`,
      name: "",
      date: "",
    };
    onChange({ rollforwardInquiries: [...data.rollforwardInquiries, newInquiry] });
  };

  const removeInquiry = (id: string) => {
    onChange({ rollforwardInquiries: data.rollforwardInquiries.filter((i) => i.id !== id) });
  };

  const updateInquiry = (id: string, field: string, value: string) => {
    onChange({
      rollforwardInquiries: data.rollforwardInquiries.map((i) =>
        i.id === id ? { ...i, [field]: value } : i
      ),
    });
  };

  const addRollforwardStep = () => {
    const newStep = {
      id: `rf-step-${Date.now()}`,
      stepDescription: "",
      natureOfProcedure: "",
      procedureDescription: "",
    };
    onChange({ rollforwardSteps: [...data.rollforwardSteps, newStep] });
  };

  const removeRollforwardStep = (id: string) => {
    onChange({ rollforwardSteps: data.rollforwardSteps.filter((s) => s.id !== id) });
  };

  const updateRollforwardStep = (id: string, field: string, value: string) => {
    onChange({
      rollforwardSteps: data.rollforwardSteps.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    });
  };

  const copyInterimProcedures = () => {
    const copiedSteps = data.operatingSteps.map((step, index) => ({
      id: `rf-copied-${Date.now()}-${index}`,
      stepDescription: step.stepDescription,
      natureOfProcedure: step.natureOfProcedure,
      procedureDescription: step.procedureDescription,
    }));
    onChange({ rollforwardSteps: [...data.rollforwardSteps, ...copiedSteps] });
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Rollforward</h2>
      </div>
      <div className="p-6 space-y-8">
        {/* Length of Rollforward Period */}
        <div className="space-y-4">
          <Label className="text-white">What is the length of the rollforward period?</Label>
          <div className="flex items-center gap-4">
            <div className="flex-1 space-y-2">
              <Label className="text-sm text-white/60">From Date</Label>
              <Input
                type="date"
                value={data.rollforwardFromDate}
                onChange={(e) => onChange({ rollforwardFromDate: e.target.value })}
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label className="text-sm text-white/60">To Date</Label>
              <Input
                type="date"
                value={data.rollforwardToDate}
                onChange={(e) => onChange({ rollforwardToDate: e.target.value })}
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
          </div>
        </div>

        {/* Rollforward Inquiry */}
        <div className="space-y-4 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">Rollforward Inquiry</h3>

          {/* Question i */}
          <div className="space-y-4">
            <Label className="text-white">
              i. Who did we inquire with to identify if significant changes to the control or
              process have occurred since interim testing?
            </Label>

            <div className="border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-black/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                      Name of respective SPOC
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                      Date of Inquiry
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rollforwardInquiries.map((inquiry) => (
                    <tr key={inquiry.id} className="border-t border-white/10">
                      <td className="px-4 py-3">
                        <Input
                          value={inquiry.name}
                          onChange={(e) => updateInquiry(inquiry.id, "name", e.target.value)}
                          className="bg-black/20 border-white/10 text-white"
                          placeholder="Enter name"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="date"
                          value={inquiry.date}
                          onChange={(e) => updateInquiry(inquiry.id, "date", e.target.value)}
                          className="bg-black/20 border-white/10 text-white"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeInquiry(inquiry.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="outline" onClick={addInquiry} className="border-white/10 text-white hover:bg-white/10">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </div>

          {/* Question ii */}
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">
                ii. Have there been any changes in the ICFR (internal control over financial
                reporting) that would impact the design and operating effectiveness of control?
              </Label>
              <Select
                value={data.hasIcfrChanges ? "Yes" : "No"}
                onValueChange={(value) => onChange({ hasIcfrChanges: value === "Yes" })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {data.hasIcfrChanges && (
              <div className="space-y-4 pl-4 border-l-2 border-primary">
                <RichTextEditor
                  label="What were the changes?"
                  value={data.icfrChanges}
                  onChange={(content) => onChange({ icfrChanges: content })}
                  placeholder="Describe the changes..."
                />
                <RichTextEditor
                  label="Additional procedures performed:"
                  value={data.additionalProcedures}
                  onChange={(content) => onChange({ additionalProcedures: content })}
                  placeholder="Describe additional procedures..."
                />
              </div>
            )}
          </div>

          {/* Question iii */}
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">
                iii. Are the relevant general IT control(s) that the automated control is dependent
                upon operating effectively?
              </Label>
              <Select
                value={data.gitcOperatingEffectively ? "Yes" : "No"}
                onValueChange={(value) =>
                  onChange({ gitcOperatingEffectively: value === "Yes" })
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <RichTextEditor
              value={data.gitcDetails}
              onChange={(content) => onChange({ gitcDetails: content })}
              placeholder="Provide additional details..."
            />
          </div>

          {/* Question iv */}
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">
                iv. Is inquiry & testing performed sufficient for rollforward?
              </Label>
              <Select
                value={data.isInquirySufficient ? "Yes" : "No"}
                onValueChange={(value) => onChange({ isInquirySufficient: value === "Yes" })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!data.isInquirySufficient && (
              <div className="space-y-4 pl-4 border-l-2 border-primary">
                <h4 className="text-sm font-medium text-white">
                  Procedures performed to evaluate the operating effectiveness of control:
                </h4>

                {/* Copy from Interim Procedures */}
                {data.operatingSteps.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={copyInterimProcedures}
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Interim Procedures
                  </Button>
                )}

                {/* Steps from detailed description OR single procedure block */}
                {controlSteps.length > 0 ? (
                  <div className="space-y-4">
                    {controlSteps.map((step, index) => (
                      <div
                        key={step.id}
                        className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4"
                      >
                        <h5 className="text-sm font-semibold text-white">Step {index + 1}</h5>
                        <div
                          className="text-sm text-white/60 mb-4 p-3 bg-black/40 rounded-lg"
                          dangerouslySetInnerHTML={{ __html: step.content }}
                        />

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-white">Nature of testing procedure</Label>
                            <Select
                              value={
                                data.rollforwardSteps.find((s) => s.id === `rf-${step.id}`)
                                  ?.natureOfProcedure || ""
                              }
                              onValueChange={(value) => {
                                const existingStep = data.rollforwardSteps.find(
                                  (s) => s.id === `rf-${step.id}`
                                );
                                if (existingStep) {
                                  updateRollforwardStep(`rf-${step.id}`, "natureOfProcedure", value);
                                } else {
                                  onChange({
                                    rollforwardSteps: [
                                      ...data.rollforwardSteps,
                                      {
                                        id: `rf-${step.id}`,
                                        stepDescription: step.content,
                                        natureOfProcedure: value,
                                        procedureDescription: "",
                                      },
                                    ],
                                  });
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select nature of procedure" />
                              </SelectTrigger>
                              <SelectContent className="bg-black/90 border-white/10">
                                {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <RichTextEditor
                            label="Procedure Description"
                            value={
                              data.rollforwardSteps.find((s) => s.id === `rf-${step.id}`)
                                ?.procedureDescription || ""
                            }
                            onChange={(content) => {
                              const existingStep = data.rollforwardSteps.find(
                                (s) => s.id === `rf-${step.id}`
                              );
                              if (existingStep) {
                                updateRollforwardStep(`rf-${step.id}`, "procedureDescription", content);
                              } else {
                                onChange({
                                  rollforwardSteps: [
                                    ...data.rollforwardSteps,
                                    {
                                      id: `rf-${step.id}`,
                                      stepDescription: step.content,
                                      natureOfProcedure: "",
                                      procedureDescription: content,
                                    },
                                  ],
                                });
                              }
                            }}
                            placeholder="Describe the procedure..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Single procedure block when detailed description is off */}
                    <div className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
                      <div className="space-y-2">
                        <Label className="text-white">Nature of testing procedure</Label>
                        <Select
                          value={data.rollforwardSteps[0]?.natureOfProcedure || ""}
                          onValueChange={(value) => {
                            if (data.rollforwardSteps.length > 0) {
                              updateRollforwardStep(data.rollforwardSteps[0].id, "natureOfProcedure", value);
                            } else {
                              onChange({
                                rollforwardSteps: [
                                  {
                                    id: `rf-default-${Date.now()}`,
                                    stepDescription: "",
                                    natureOfProcedure: value,
                                    procedureDescription: "",
                                  },
                                ],
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select nature of procedure" />
                          </SelectTrigger>
                          <SelectContent className="bg-black/90 border-white/10">
                            {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <RichTextEditor
                        label="Procedure Description"
                        value={data.rollforwardSteps[0]?.procedureDescription || ""}
                        onChange={(content) => {
                          if (data.rollforwardSteps.length > 0) {
                            updateRollforwardStep(
                              data.rollforwardSteps[0].id,
                              "procedureDescription",
                              content
                            );
                          } else {
                            onChange({
                              rollforwardSteps: [
                                {
                                  id: `rf-default-${Date.now()}`,
                                  stepDescription: "",
                                  natureOfProcedure: "",
                                  procedureDescription: content,
                                },
                              ],
                            });
                          }
                        }}
                        placeholder="Describe the procedure..."
                      />
                    </div>

                    {/* Additional rollforward steps */}
                    {data.rollforwardSteps.slice(1).map((step, index) => (
                      <div
                        key={step.id}
                        className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4"
                      >
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-semibold text-white">
                            Procedure {index + 2}
                          </h5>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRollforwardStep(step.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white">Nature of testing procedure</Label>
                          <Select
                            value={step.natureOfProcedure}
                            onValueChange={(value) =>
                              updateRollforwardStep(step.id, "natureOfProcedure", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select nature of procedure" />
                            </SelectTrigger>
                            <SelectContent className="bg-black/90 border-white/10">
                              {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <RichTextEditor
                          label="Procedure Description"
                          value={step.procedureDescription}
                          onChange={(content) =>
                            updateRollforwardStep(step.id, "procedureDescription", content)
                          }
                          placeholder="Describe the procedure..."
                        />
                      </div>
                    ))}

                    <Button variant="outline" onClick={addRollforwardStep} className="border-white/10 text-white hover:bg-white/10">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Procedure
                    </Button>
                  </>
                )}

                {/* Perform tests */}
                <div className="space-y-3 border-t border-white/10 pt-6">
                  <h4 className="text-sm font-semibold text-white">
                    Perform tests of operating effectiveness
                  </h4>
                  <p className="text-sm text-white/60">
                    For each activity, we document the procedures performed to test the operating
                    effectiveness.
                  </p>
                  <ControlDocumentManager
                    controlId={data.controlId}
                    workspaceLinked={data.workspaceLinked}
                    documents={data.rollforwardOperatingTestsDocuments || []}
                    onDocumentsChange={handleRollforwardDocumentsChange}
                    textResponse={data.rollforwardOperatingTestsTextResponse}
                    onTextResponseChange={handleRollforwardTextResponseChange}
                    label="Rollforward Operating Tests"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Question v: Operating Effectiveness Testing Conclusion */}
        <div className="space-y-4 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">
            v. Operating Effectiveness Testing Conclusion
          </h3>

          <div className="flex items-center gap-4">
            <Label className="flex-1 text-white">Is the control operating effectively?</Label>
            <Select
              value={data.rollforwardOperatingEffective}
              onValueChange={(value: "Yes" | "No" | "Not Yet Concluded") =>
                onChange({ rollforwardOperatingEffective: value })
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Not Yet Concluded">Not Yet Concluded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};

